/**
 * DepthSlider — native `<input type="range">` with `min=-1 max=1`.
 *
 * Nothing fancy here: the browser handles drag, click-to-position,
 * keyboard controls, and accessibility. Double-click resets to 0
 * (the natural null for bipolar depth).
 */

export interface DepthSliderOptions {
  /** Initial value, -1..+1. */
  readonly value: number;
  /** Called on every change with the clamped new value. */
  readonly onChange: (value: number) => void;
  /** ARIA label. Defaults to "Depth". */
  readonly label?: string;
}

const MIN = -1;
const MAX = 1;

export class DepthSlider {
  readonly element: HTMLInputElement;

  private readonly onChange: (v: number) => void;

  constructor(opts: DepthSliderOptions) {
    const value = clamp(opts.value);
    this.onChange = opts.onChange;

    this.element = document.createElement('input');
    this.element.type = 'range';
    this.element.min = String(MIN);
    this.element.max = String(MAX);
    this.element.step = '0.01';
    this.element.value = String(value);
    this.element.className = 'depth-slider range-themed';
    this.element.setAttribute('aria-label', opts.label ?? 'Depth');

    this.element.addEventListener('input', this.onInput);
    this.element.addEventListener('dblclick', this.onDoubleClick);
  }

  /** Set the value programmatically (preset load, etc.). */
  setValue(v: number): void {
    this.element.value = String(clamp(v));
  }

  dispose(): void {
    this.element.removeEventListener('input', this.onInput);
    this.element.removeEventListener('dblclick', this.onDoubleClick);
  }

  private onInput = (): void => {
    this.onChange(clamp(parseFloat(this.element.value)));
  };

  /** Double-click resets to 0 — the natural null for bipolar depth. */
  private onDoubleClick = (e: MouseEvent): void => {
    e.preventDefault();
    this.setValue(0);
    this.onChange(0);
  };
}

function clamp(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.max(MIN, Math.min(MAX, v));
}
